// The shared live hook for the population widgets (Ranking now; Filter, Comparison, Player
// Similarity, Radar next): the canvas player's league → its built seasons → one season's file.
// The loader and Ben's rules for it are in `live/group-players.ts`. Live only — each widget keeps
// its snapshot hook for the normal site.

import { KNOWN_LEAGUE_NAMES } from "../live/known-leagues.js";
import type { PlayerId } from "../data/players.js";
import { liveApi } from "../live/api.js";
import { rememberCurrentSeason } from "../live/player-gender.js";
import { useLiveQueries, useLiveQuery } from "../live/query.js";
import { loadFileSeasons } from "../live/stats-files.js";
import { loadGroupFile, loadHerLeague, noGroupLabel, pickGroupSeason, type GroupFile, type HerLeague } from "../live/group-players.js";

/** One league-season's group, or why there's none. `seasons` = that league's built seasons
 *  (newest first) — the widget's season picker. */
export type LiveGroupFile =
  | { kind: "file"; league: HerLeague & { label: string }; seasons: number[]; seasonId: number; group: GroupFile }
  | { kind: "none"; label: string; seasons: number[]; seasonId: number };

/**
 * The group for `playerId` (the canvas player) in `season` (a season id as a string; null or one
 * that league hasn't built → her current season). Suspends while loading (LiveBoundary shows
 * "Loading real data…"). Calls: her league (3 in parallel — `/player-seasons`,
 * `/player-current-league`, `/competitions`), the league's built seasons, then the file + its
 * season's movers. A season switch asks only for that season's file + movers.
 */
export function useLiveGroupFile(playerId: PlayerId, season: string | null): LiveGroupFile {
  const league = useLiveQuery(`group:league:${playerId}`, () =>
    loadHerLeague(liveApi(), playerId, KNOWN_LEAGUE_NAMES),
  );
  if (league === null) throw new Error(`Player ${playerId} has no seasons`);
  const competitionId = league.competitionId;
  const built = useLiveQuery(competitionId === null ? null : `files:seasons:${competitionId}`, () =>
    loadFileSeasons(liveApi(), competitionId as number),
  );
  const seasons = built ? built.seasons.map((s) => s.seasonId) : [];
  const picked = pickGroupSeason(season, league.seasonId, seasons);
  // What "her current one" means on a tile, for the drop rule's same-season check (player-gender.ts).
  // Only once the built seasons are known, so it's the season the tile really opens on.
  if (season === null && built) rememberCurrentSeason(playerId, String(picked.seasonId));
  const fileKey = competitionId !== null && picked.built ? `group:file:${competitionId}:${picked.seasonId}` : null;
  const group = useLiveQuery(fileKey, async () => {
    const g = await loadGroupFile(liveApi(), competitionId as number, picked.seasonId);
    // Listed as built but missing = a broken listing — thrown, never an empty group.
    if (g === null) throw new Error(`Stats file ${competitionId}/${picked.seasonId} is listed as built, but isn't there`);
    return g;
  });
  if (group === null || league.label === null) {
    return { kind: "none", label: noGroupLabel(league, picked.seasonId), seasons, seasonId: picked.seasonId };
  }
  return { kind: "file", league: { ...league, label: league.label }, seasons, seasonId: picked.seasonId, group };
}

/**
 * Each player's group in her season, all players at once (a merged Radar: one per player).
 * `seasons[i]` = the season her tile showed at the merge (null / missing = her current one), so
 * the same player on two seasons is a year-on-year (Ben, 2026-10-16). The same steps and keys as `useLiveGroupFile`, so a player the widget already showed
 * isn't asked again; every step starts for all players before the widget waits (3 waits in all,
 * not 3 per player). Players in one league share its calls.
 */
export function useLiveGroupFiles(
  playerIds: readonly PlayerId[],
  seasons: readonly (string | null | undefined)[] = [],
): LiveGroupFile[] {
  const leagues = useLiveQueries(
    playerIds.map((id) => ({
      key: `group:league:${id}`,
      load: () => loadHerLeague(liveApi(), id, KNOWN_LEAGUE_NAMES),
    })),
  );
  leagues.forEach((l, i) => {
    if (l === null) throw new Error(`Player ${playerIds[i]} has no seasons`);
  });
  const her = leagues as HerLeague[];
  const competitions = [...new Set(her.map((l) => l.competitionId).filter((c): c is number => c !== null))];
  const builtLists = useLiveQueries(competitions.map((c) => ({ key: `files:seasons:${c}`, load: () => loadFileSeasons(liveApi(), c) })));
  const seasonsOf = (c: number | null) => (c === null ? [] : builtLists[competitions.indexOf(c)]!.seasons.map((s) => s.seasonId));
  const picks = her.map((l, i) => pickGroupSeason(seasons[i] ?? null, l.seasonId, seasonsOf(l.competitionId)));
  // What "her current one" means, for the drop rule's same-season check (as useLiveGroupFile).
  picks.forEach((p, i) => {
    if ((seasons[i] ?? null) === null && her[i]!.competitionId !== null) rememberCurrentSeason(playerIds[i]!, String(p.seasonId));
  });
  const fileKeys = [
    ...new Set(her.flatMap((l, i) => (l.competitionId !== null && picks[i]!.built ? [`${l.competitionId}:${picks[i]!.seasonId}`] : []))),
  ];
  const groups = useLiveQueries(
    fileKeys.map((k) => ({
      key: `group:file:${k}`,
      load: async () => {
        const [c, s] = k.split(":").map(Number) as [number, number];
        const g = await loadGroupFile(liveApi(), c, s);
        if (g === null) throw new Error(`Stats file ${c}/${s} is listed as built, but isn't there`);
        return g;
      },
    })),
  );
  return her.map((league, i) => {
    const picked = picks[i]!;
    const seasons = seasonsOf(league.competitionId);
    const at = league.competitionId !== null && picked.built ? fileKeys.indexOf(`${league.competitionId}:${picked.seasonId}`) : -1;
    if (at === -1 || league.label === null) {
      return { kind: "none", label: noGroupLabel(league, picked.seasonId), seasons, seasonId: picked.seasonId };
    }
    return { kind: "file", league: { ...league, label: league.label }, seasons, seasonId: picked.seasonId, group: groups[at]! };
  });
}
